import React from "react";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Typography,
  List,
  ListItem,
  CircularProgress,
  Tooltip,
  Theme,
} from "@mui/material";

import { Panel, InfoTooltip } from "../../components";
import ComponentLoader from "../../components/ComponentLoader/ComponentLoader";
import EmptyState from "../../components/EmptyState";
import {
  useCustomerActivitiesPanel,
  FormattedCustomerActivity,
  ActivityType,
} from "./CustomerActivityPanel.hooks";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import {
  Cart,
  CursorDefaultClick,
  Forms,
  InfoOutline,
  MailOpen,
  Refresh02,
  Search,
  UserMinus,
} from "@tiny/icons";

const activityIcons: Record<string, JSX.Element> = {
  UNSUBSCRIBE: <UserMinus color="error" fontSize="inherit" />,
  OPEN: <MailOpen color="info" fontSize="inherit" />,
  CLICK: <CursorDefaultClick color="primary" fontSize="inherit" />,
  ORDER: <Cart color="success" fontSize="inherit" />,
  FORM_SUBMISSION: <Forms color="secondary" fontSize="inherit" />,
};

const activityText: Record<string, string> = {
  UNSUBSCRIBE: "unsubscribed",
  OPEN: "opened",
  CLICK: "clicked",
  ORDER: "ordered from",
  FORM_SUBMISSION: "submitted form",
};

const tabDisplayLabels: Record<ActivityType | "ALL", string> = {
  ALL: "All",
  OPEN: "Opens",
  CLICK: "Clicks",
  UNSUBSCRIBE: "Unsubscribes",
  ORDER: "Orders",
  FORM_SUBMISSION: "Form Submissions",
};

function CustomerActivitiesPanel({
  email = undefined,
}: {
  email?: string;
}): JSX.Element {
  const {
    tabValue,
    handleTabChange,
    activities,
    isLoading,
    isFetching,
    hasMore,
    handleRefresh,
    handleActivityClick,
    loadMore,
    tabLabels,
  } = useCustomerActivitiesPanel({ email });

  const lastActivityElementRef = useInfiniteScroll({
    isFetching,
    hasMore,
    loadMore,
  });

  const renderActivity = (
    activity: FormattedCustomerActivity,
    index: number
  ): JSX.Element => {
    const isLastElement = index === activities.length - 1;

    const getActivityText = () => {
      if (activity.customerActivityType === "ORDER" && !activity.name) {
        return "ordered";
      }
      return activityText[activity.customerActivityType] || "";
    };

    return (
      <ListItem
        key={`${activity.id}-${activity.eventTime}`}
        ref={isLastElement ? lastActivityElementRef : undefined}
        onClick={() => handleActivityClick(activity)}
        sx={{
          display: "flex",
          alignItems: "center",
          py: 2,
          cursor: "pointer",
          position: "relative",
          pl: 4,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        {/* Timeline Line */}
        <Box
          sx={{
            position: "absolute",
            left: 32,
            top: 0,
            bottom: 0,
            width: "1px",
            backgroundColor: "grey.300",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            zIndex: 2,
            backgroundColor: "grey.20",
            borderRadius: "50%",
            mr: 4,
            fontSize: "16px",
            p: 2,
            my: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {activityIcons[activity.customerActivityType] || (
            <InfoOutline color="disabled" fontSize="inherit" />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            flexGrow: 1,
            gap: 1,
          }}
        >
          <Typography
            variant="label2R"
            color="text.secondary"
            sx={{ minWidth: "70px" }}
          >
            {activity.timestamp}
          </Typography>
          <Tooltip
            componentsProps={{
              popper: {
                sx: {
                  "& .MuiTooltip-tooltip": {
                    padding: 2,
                  },
                },
              },
            }}
            children={
              <Typography
                variant="label2"
                color="text.primary"
                sx={{
                  maxWidth: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: activity.email?.length > 30 ? "help" : "default",
                }}
              >
                {activity.email?.length > 30
                  ? `${activity.email.slice(0, 27)}...`
                  : activity.email}
              </Typography>
            }
            title={activity.email}
          ></Tooltip>
          <Typography variant="label2R" color="text.secondary">
            {getActivityText()} {/* Clean handling of activity text */}
          </Typography>
          {activity.name && (
            <Typography variant="caption" color="text.secondary">
              {activity.type.toLowerCase()}:
            </Typography>
          )}
          <Typography variant="caption" color="text.primary">
            {activity.name}
          </Typography>
        </Box>
      </ListItem>
    );
  };

  const renderContent = () => {
    if (isLoading && activities.length === 0) {
      return <ComponentLoader height="300px" />;
    }

    if (!isLoading && activities.length === 0) {
      return (
        <Box
          sx={{
            height: "100%",
          }}
        >
          <EmptyState
            Icon={
              <Search
                sx={(theme: Theme) => ({
                  color: "grey.80",
                  width: theme.spacing(10),
                  height: theme.spacing(10),
                })}
              />
            }
            title="No customer activities"
            message="There are no recent customer activities for the selected filter."
            height="100%"
          />
        </Box>
      );
    }

    return (
      <>
        <List disablePadding>
          {activities.map((activity, index) => renderActivity(activity, index))}
        </List>
        {isFetching && activities.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </>
    );
  };

  return (
    <Panel
      title="Customer Activity"
      actions={
        <Box sx={{ display: "flex" }}>
          <IconButton
            aria-label="refresh"
            size="small"
            sx={{ mr: 1 }}
            onClick={handleRefresh}
            disabled={isFetching || isLoading}
          >
            <Refresh02 />
          </IconButton>
          <InfoTooltip
            message="Customer interactions across your campaigns, workflows, and forms"
            variant="white"
            noInfoIcon
          >
            <IconButton sx={{ p: 0 }} size="small">
              <InfoOutline />
            </IconButton>
          </InfoTooltip>
        </Box>
      }
    >
      <Box sx={{ width: "100%", mt: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Filter activities by type"
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 5 }}
          >
            {tabLabels.map((label, index) => (
              <Tab
                key={label}
                sx={{ textTransform: "capitalize", minWidth: "auto" }}
                label={tabDisplayLabels[label]}
                id={`activity-tab-${index}`}
                value={index}
                aria-controls={`activity-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        <Box
          px={2}
          py={1}
          sx={{
            height: "400px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Panel>
  );
}

export default CustomerActivitiesPanel;
